<script setup>
import { defineEmits, defineProps, reactive, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { to } from "await-to-js";
import { useEnvStore } from "@dutypay/front-end-design-system/src/stores";
import { interceptor } from "@dutypay/front-end-design-system/src/utilities";
import { useTwoWayBinding } from '@dutypay/front-end-design-system';
import { useOneTimePasswordValidation } from "@dutypay/front-end-design-system/src/validations";
import { useRoute, useRouter } from 'vue-router';

const envStore = useEnvStore();
const { t } = useI18n();
const emit = defineEmits(["update", "reset-login-data", "go-to"]);
const props = defineProps({
  modelValue: { type: Object, required: true },
});
const loginData = useTwoWayBinding(props, emit, "modelValue");

const notification = reactive({
  sendEmail: {
    success: false
  },
  verifyEmailOTP: {
    asyncError: false
  }
});
const isLoadingAuthEmail = ref(false);
const isResendDisabled = ref(true);
const countdown = ref(20);

// Verify Email OTP from input
async function verifyEmailOTP() {
  notification.verifyEmailOTP.asyncError = false;
  let err, mfaModes;
  [err, mfaModes] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/check-mfa`, { ...loginData.value }));
  if (err) {
    notification.verifyEmailOTP.asyncError = true;
    return;
  }
  loginData.value.mfaModes = mfaModes.data.result.mfaModes;
  emit("go-to", "MFAChoice");
  return;
}

function countDownTimer() {
  if (countdown.value > 0) {
    setTimeout(() => {
      countdown.value -= 1;
      countDownTimer();
    }, 1000);
  } else {
    isResendDisabled.value = false;
  }
}
// Send Email with OTP and Authentication Link
async function sendEmailOTP() {
  isLoadingAuthEmail.value = true;
  isResendDisabled.value = true;
  notification.sendEmail.success = false;
  notification.verifyEmailOTP.asyncError = false;
  let err;
  [err] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/send/auth-email`, { ...loginData.value }));
  isLoadingAuthEmail.value = false;
  if (err) {
    isResendDisabled.value = false;
  }
  countdown.value = 20;
  countDownTimer();
  notification.sendEmail.success = true
}

onMounted(async () => {
  const router = useRouter();
  const route = useRoute();
  loginData.value.emailOTP = "";
  await router.isReady();
  // Extract Query Params from route
  let queryParams = route.query;
  // Check for emailAuthCode and email in URL Query Params
  if (queryParams.emailAuthCode && queryParams.email) {
    loginData.value.email = queryParams.email;
    //Check if user exists
    isLoadingAuthEmail.value = true;
    isResendDisabled.value = true;
    notification.sendEmail.success = false;
    notification.verifyEmailOTP.asyncError = false;
    let err;
    [err] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/send/auth-email`, { ...loginData.value }));
    isLoadingAuthEmail.value = false;
    if (err) {
      isResendDisabled.value = false;
    }
    countdown.value = 20;
    countDownTimer();
    notification.sendEmail.success = true
    loginData.value.emailOTP = queryParams.emailAuthCode;
    countDownTimer();
    return;
  }
  await sendEmailOTP();
});

const validation = {
    emailOTP: [
      useOneTimePasswordValidation()
    ],
}

</script>
<script>
export default {
  name: "EmailOTPForm"
}
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.EmailOTPForm.emailOTPForm.title") }}</h2>

    <DpNotification
      type="success"
      :visibility="notification.sendEmail.success"
      :title="t('sso.pages.LoginStages.EmailOTPForm.notification.sendEmail.success.title')"
      :message="t('sso.pages.LoginStages.EmailOTPForm.notification.sendEmail.success.message', { primaryEmailAddressPlaceholder: loginData.email })">
      <template #controls>
        <dp-button
          purpose="secondaryAction"
          class="full-width"
          :disabled="isResendDisabled"
          style="margin: 1em 0;"
          @click="sendEmailOTP">
            <span v-if="isResendDisabled">{{
                t("sso.pages.LoginStages.EmailOTPForm.resendEmailInSeconds.buttonLabel", { secondsPlaceholder: countdown })
              }}</span>
          <span v-else>{{ t("sso.pages.LoginStages.EmailOTPForm.resendEmail.buttonLabel") }}</span>
        </dp-button>
      </template>

      {{ t('sso.pages.LoginStages.EmailOTPForm.notification.sendEmail.success.message', { primaryEmailAddressPlaceholder: loginData.email }) }}
    </DpNotification>

    <DpNotification
      type="error"
      :visibility="notification.verifyEmailOTP.asyncError"
      :title="t('sso.pages.LoginStages.EmailOTPForm.notification.verifyEmailOTP.asyncError.title')"
      :message="t('sso.pages.LoginStages.EmailOTPForm.notification.verifyEmailOTP.asyncError.message')"
    ></DpNotification>

    <DpForm
      v-model="loginData"
      v-loading="isLoadingAuthEmail"
      @submit="verifyEmailOTP"
      :showValidationNotification="false"
      :validation="validation"
      >
       <p>{{ t("sso.pages.LoginStages.EmailOTPForm.unlockWith.label") }}</p>
      <DpInput
          v-model="loginData.emailOTP"
          :validation="validation.emailOTP"
          id="emailOTP"
          autocomplete="off"
          :label="t('sso.pages.LoginStages.EmailOTPForm.emailOTP.input.label')"
          :placeholder="t('sso.pages.LoginStages.EmailOTPForm.emailOTP.input.placeholder')" />

      <template #control="{ validate }">
            <DpButton
                purpose="primaryAction"
                size="large"
                @click="validate">
                {{ t("sso.pages.LoginStages.EmailOTPForm.confirmCode.buttonLabel") }}
            </DpButton>
      </template>
    </DpForm>
  </div>
</template>

<i18n>
{
  "ar-AE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "خطأ",
        "message": "لا يمكن تأكيد رمز المصادقة."
      },
      "verifyEmailOTP": {
        "message": "لا يمكن تأكيد رمز المصادقة.",
        "title": "خطأ",
        "asyncError": {
          "message": "رمز المصادقة غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {
        "title": "عمل ناجح",
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل."
      },
      "sendEmail": {
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل.",
        "success": {
          "message": "تم إرسال رمز التحقق عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        },
        "title": "عمل ناجح"
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-DZ": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "رمز التوثيق غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "تم إرسال كود التوثيق عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-EG": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "خطأ",
        "message": "لا يمكن تأكيد رمز المصادقة."
      },
      "verifyEmailOTP": {
        "message": "لا يمكن تأكيد رمز المصادقة.",
        "title": "خطأ",
        "asyncError": {
          "message": "الرمز التوثيقي غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {
        "title": "عمل ناجح",
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل."
      },
      "sendEmail": {
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل.",
        "success": {
          "message": "تم إرسال كود التحقق عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        },
        "title": "عمل ناجح"
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-LB": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "الرمز التوثيقي غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "تم إرسال كود التأكيد بواسطة البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-MA": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "تم إرسال رمز التحقق بالبريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "ادخل رمز التأكيد",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-SA": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "خطأ",
        "message": "لا يمكن تأكيد رمز المصادقة."
      },
      "verifyEmailOTP": {
        "message": "لا يمكن تأكيد رمز المصادقة.",
        "title": "خطأ",
        "asyncError": {
          "message": "رمز التحقق غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {
        "title": "عمل ناجح",
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل."
      },
      "sendEmail": {
        "message": "تم إرسال رمز المصادقة عبر البريد الإلكتروني. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة للتسجيل.",
        "success": {
          "message": "تم إرسال كود التحقق عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        },
        "title": "عمل ناجح"
      }
    },
    "resendEmail": {
      "buttonLabel": "أعد الإرسال"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-SY": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "تم إرسال كود التأكيد عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-TN": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "كود التوثيق غير صالح",
          "title": "خطأ"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "تم إرسال كود التوثيق عبر البريد الإلكتروني إلى {primaryEmailAddressPlaceholder}.",
          "title": "عمل ناجح"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "emailOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من البريد الإلكتروني"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    },
    "emailOTPForm": {
      "title": "رمز المصادقة عن طريق البريد الإلكتروني"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "bg-BG": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "грешка",
        "message": "Кодът за удостоверяване не може да бъде потвърден."
      },
      "verifyEmailOTP": {
        "message": "Кодът за удостоверяване не може да бъде потвърден.",
        "title": "грешка",
        "asyncError": {
          "message": "Кодът за удостоверяване е невалиден",
          "title": "грешка"
        }
      },
      "emailSent": {
        "title": "Успешно действие",
        "message": "Кодът за удостоверяване беше изпратен по имейл. Може да се използва само веднъж и в рамките на ограничено време за регистрация."
      },
      "sendEmail": {
        "message": "Кодът за удостоверяване беше изпратен по имейл. Може да се използва само веднъж и в рамките на ограничено време за регистрация.",
        "success": {
          "message": "Кодът за удостоверяване е изпратен по имейл до {primaryEmailAddressPlaceholder}.",
          "title": "Успешно действие"
        },
        "title": "Успешно действие"
      }
    },
    "resendEmail": {
      "buttonLabel": "Изпрати отново"
    },
    "unlockWith": {
      "label": "Въведете и потвърдете получения код за удостоверяване."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Въведете код за удостоверяване",
        "label": "Код за удостоверяване от имейла"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Преизпрати след {secondsPlaceholder} секунди"
    },
    "emailOTPForm": {
      "title": "Код за удостоверяване по имейл"
    },
    "confirmCode": {
      "buttonLabel": "Потвърждение"
    }
  },
  "bs-BA": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Greška",
        "message": "Nije moguće potvrditi autentifikacijski kôd."
      },
      "verifyEmailOTP": {
        "message": "Nije moguće potvrditi autentifikacijski kôd.",
        "title": "Greška",
        "asyncError": {
          "message": "Kod za autentifikaciju je nevažeći",
          "title": "Greška"
        }
      },
      "emailSent": {
        "title": "Uspješno obavljeno",
        "message": "Kod za autentifikaciju je poslan putem e-pošte. Može se koristiti samo jednom iu ograničenom vremenu za registraciju."
      },
      "sendEmail": {
        "message": "Kod za autentifikaciju je poslan putem e-pošte. Može se koristiti samo jednom iu ograničenom vremenu za registraciju.",
        "success": {
          "message": "Autentifikacijski kod poslan je e-mailom na {primaryEmailAddressPlaceholder}.",
          "title": "Uspješno obavljeno"
        },
        "title": "Uspješno obavljeno"
      }
    },
    "resendEmail": {
      "buttonLabel": "Pošalji ponovo"
    },
    "unlockWith": {
      "label": "Unesite i potvrdite primljeni kod za autentifikaciju."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Unesite kod za autentifikaciju",
        "label": "Kod za autentifikaciju iz e-maila"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    },
    "emailOTPForm": {
      "title": "Autentifikacijski kod e-poštom"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdite"
    }
  },
  "cs-CZ": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Chyba",
        "message": "Nelze potvrdit ověřovací kód."
      },
      "verifyEmailOTP": {
        "message": "Nelze potvrdit ověřovací kód.",
        "title": "Chyba",
        "asyncError": {
          "message": "Ověřovací kód je neplatný",
          "title": "Chyba"
        }
      },
      "emailSent": {
        "title": "Úspěšná akce",
        "message": "Ověřovací kód byl zaslán e-mailem. Lze jej použít pouze jednou a během omezené doby pro registraci."
      },
      "sendEmail": {
        "message": "Ověřovací kód byl zaslán e-mailem. Lze jej použít pouze jednou a během omezené doby pro registraci.",
        "success": {
          "message": "Ověřovací kód byl odeslán e-mailem na {primaryEmailAddressPlaceholder}.",
          "title": "Úspěšná akce"
        },
        "title": "Úspěšná akce"
      }
    },
    "resendEmail": {
      "buttonLabel": "Poslat znovu"
    },
    "unlockWith": {
      "label": "Zadejte a potvrďte přijatý ověřovací kód."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Zadejte ověřovací kód",
        "label": "Autentizační kód z e-mailu"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Znovu odeslat za {secondsPlaceholder} sekundy"
    },
    "emailOTPForm": {
      "title": "Ověřovací kód e-mailem"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdit"
    }
  },
  "da-DK": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fejl",
        "message": "Godkendelseskoden kunne ikke bekræftes."
      },
      "verifyEmailOTP": {
        "message": "Godkendelseskoden kunne ikke bekræftes.",
        "title": "Fejl",
        "asyncError": {
          "message": "Godkendelseskoden er ugyldig",
          "title": "Fejl"
        }
      },
      "emailSent": {
        "title": "Vellykket handling",
        "message": "Godkendelseskoden blev sendt via e-mail. Den kan kun bruges én gang og inden for en begrænset tid til registrering."
      },
      "sendEmail": {
        "message": "Godkendelseskoden blev sendt via e-mail. Den kan kun bruges én gang og inden for en begrænset tid til registrering.",
        "success": {
          "message": "Godkendelseskoden er sendt via e-mail til {primaryEmailAddressPlaceholder}.",
          "title": "Vellykket handling"
        },
        "title": "Vellykket handling"
      }
    },
    "resendEmail": {
      "buttonLabel": "Send igen"
    },
    "unlockWith": {
      "label": "Indtast og bekræft den modtagne godkendelseskode."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Indtast godkendelseskode",
        "label": "Autentificeringskode fra e-mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Gensend om {secondsPlaceholder} sekunder"
    },
    "emailOTPForm": {
      "title": "Godkendelseskode via e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Bekræft"
    }
  },
  "de-DE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fehler",
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden."
      },
      "verifyEmailOTP": {
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden.",
        "title": "Fehler",
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      },
      "emailSent": {
        "title": "Erfolgreiche Aktion",
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
      },
      "sendEmail": {
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden.",
        "success": {
          "message": "Der Authentifizierungscode wurde per E-Mail an {primaryEmailAddressPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "title": "Erfolgreiche Aktion"
      }
    },
    "resendEmail": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der E-Mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    },
    "emailOTPForm": {
      "title": "Authentifizierungscode per E-Mail"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-AT": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fehler",
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden."
      },
      "verifyEmailOTP": {
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden.",
        "title": "Fehler",
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      },
      "emailSent": {
        "title": "Erfolgreiche Aktion",
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
      },
      "sendEmail": {
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden.",
        "success": {
          "message": "Der Authentifizierungscode wurde per E-Mail an {primaryEmailAddressPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "title": "Erfolgreiche Aktion"
      }
    },
    "resendEmail": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der E-Mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    },
    "emailOTPForm": {
      "title": "Authentifizierungscode per E-Mail"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-CH": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fehler",
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden."
      },
      "verifyEmailOTP": {
        "message": "Der Authentifizierungscode konnte nicht bestätigt werden.",
        "title": "Fehler",
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      },
      "emailSent": {
        "title": "Erfolgreiche Aktion",
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
      },
      "sendEmail": {
        "message": "Der Authentifizierungscode wurde per E-Mail gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden.",
        "success": {
          "message": "Der Authentifizierungscode wurde per E-Mail an {primaryEmailAddressPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "title": "Erfolgreiche Aktion"
      }
    },
    "resendEmail": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der E-Mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    },
    "emailOTPForm": {
      "title": "Authentifizierungscode per E-Mail"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-LU": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "Der Authentifizierungscode ist ungültig.",
          "title": "Fehler"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "Der Authentifizierungscode wurde per E-Mail an {primaryEmailAddressPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der E-Mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    },
    "emailOTPForm": {
      "title": "Authentifizierungscode per E-Mail"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "el-GR": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Σφάλμα",
        "message": "Δεν ήταν δυνατή η επιβεβαίωση του κωδικού ελέγχου ταυτότητας."
      },
      "verifyEmailOTP": {
        "message": "Δεν ήταν δυνατή η επιβεβαίωση του κωδικού ελέγχου ταυτότητας.",
        "title": "Σφάλμα",
        "asyncError": {
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος",
          "title": "Σφάλμα"
        }
      },
      "emailSent": {
        "title": "Επιτυχημένη ενέργεια",
        "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω email. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για εγγραφή."
      },
      "sendEmail": {
        "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω email. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για εγγραφή.",
        "success": {
          "message": "Ο κωδικός επαλήθευσης έχει αποσταλεί με email στο {primaryEmailAddressPlaceholder}.",
          "title": "Επιτυχημένη ενέργεια"
        },
        "title": "Επιτυχημένη ενέργεια"
      }
    },
    "resendEmail": {
      "buttonLabel": "Στείλε ξανά"
    },
    "unlockWith": {
      "label": "Εισαγάγετε και επιβεβαιώστε τον κωδικό ελέγχου ταυτότητας που λάβατε."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης",
        "label": "Κωδικός επαλήθευσης από το email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    },
    "emailOTPForm": {
      "title": "Κωδικός ελέγχου ταυτότητας μέσω email"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    }
  },
  "el-CY": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Σφάλμα",
        "message": "Δεν ήταν δυνατή η επιβεβαίωση του κωδικού ελέγχου ταυτότητας."
      },
      "verifyEmailOTP": {
        "message": "Δεν ήταν δυνατή η επιβεβαίωση του κωδικού ελέγχου ταυτότητας.",
        "title": "Σφάλμα",
        "asyncError": {
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος",
          "title": "Σφάλμα"
        }
      },
      "emailSent": {
        "title": "Επιτυχημένη ενέργεια",
        "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω email. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για εγγραφή."
      },
      "sendEmail": {
        "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω email. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για εγγραφή.",
        "success": {
          "message": "Ο κωδικός επαλήθευσης έχει αποσταλεί μέσω email στο {primaryEmailAddressPlaceholder}.",
          "title": "Επιτυχημένη ενέργεια"
        },
        "title": "Επιτυχημένη ενέργεια"
      }
    },
    "resendEmail": {
      "buttonLabel": "Στείλε ξανά"
    },
    "unlockWith": {
      "label": "Εισαγάγετε και επιβεβαιώστε τον κωδικό ελέγχου ταυτότητας που λάβατε."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης",
        "label": "Κωδικός επαλήθευσης από το email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    },
    "emailOTPForm": {
      "title": "Κωδικός ελέγχου ταυτότητας μέσω email"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    }
  },
  "en-GB": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Error",
        "message": "The authentication code could not be confirmed."
      },
      "verifyEmailOTP": {
        "message": "The authentication code could not be confirmed.",
        "title": "Error",
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      },
      "emailSent": {
        "title": "Successful action",
        "message": "The authentication code was sent via email. It can only be used once and within a limited time for registration."
      },
      "sendEmail": {
        "message": "The authentication code was sent via email. It can only be used once and within a limited time for registration.",
        "success": {
          "message": "The authentication code has been sent by email to {primaryEmailAddressPlaceholder}.",
          "title": "Successful Action"
        },
        "title": "Successful action"
      }
    },
    "resendEmail": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    },
    "emailOTPForm": {
      "title": "Authentication Code by Email"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "en-IE": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "The authentication code has been sent by email to {primaryEmailAddressPlaceholder}.",
          "title": "Successful Action"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    },
    "emailOTPForm": {
      "title": "Authentication Code by Email"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "en-US": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Error",
        "message": "The authentication code could not be confirmed."
      },
      "verifyEmailOTP": {
        "message": "The authentication code could not be confirmed.",
        "title": "Error",
        "asyncError": {
          "message": "The authentication code is invalid.",
          "title": "Error"
        }
      },
      "emailSent": {
        "title": "Successful action",
        "message": "The authentication code was sent via e-mail. It can only be used once and within a limited time for logging in."
      },
      "sendEmail": {
        "message": "The authentication code was sent via e-mail. It can only be used once and within a limited time for logging in.",
        "success": {
          "message": "The authentication code has been sent by email to {primaryEmailAddressPlaceholder}.",
          "title": "Successful Action"
        },
        "title": "Successful action"
      }
    },
    "resendEmail": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    },
    "emailOTPForm": {
      "title": "Authentication Code by Email"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "es-ES": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Error",
        "message": "No se pudo confirmar el código de autenticación."
      },
      "verifyEmailOTP": {
        "message": "No se pudo confirmar el código de autenticación.",
        "title": "Error",
        "asyncError": {
          "message": "El código de autenticación es inválido",
          "title": "Error"
        }
      },
      "emailSent": {
        "title": "Listo",
        "message": "El código de autenticación se envió por correo electrónico. Solo se puede usar una vez y dentro de un tiempo limitado para el registro."
      },
      "sendEmail": {
        "message": "El código de autenticación se envió por correo electrónico. Solo se puede usar una vez y dentro de un tiempo limitado para el registro.",
        "success": {
          "message": "El código de autenticación ha sido enviado por correo electrónico a {primaryEmailAddressPlaceholder}.",
          "title": "Listo"
        },
        "title": "Listo"
      }
    },
    "resendEmail": {
      "buttonLabel": "Enviar de nuevo"
    },
    "unlockWith": {
      "label": "Introduzca y confirme el código de autenticación recibido."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Ingrese el código de autenticación",
        "label": "Código de autenticación del correo electrónico"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Volver a enviar en {secondsPlaceholder} segundos"
    },
    "emailOTPForm": {
      "title": "Código de autenticación por correo electrónico"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    }
  },
  "et-EE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Viga",
        "message": "Autentimiskoodi ei õnnestunud kinnitada."
      },
      "verifyEmailOTP": {
        "message": "Autentimiskoodi ei õnnestunud kinnitada.",
        "title": "Viga",
        "asyncError": {
          "message": "Autentimiskood on kehtetu",
          "title": "Viga"
        }
      },
      "emailSent": {
        "title": "Edukas tegevus",
        "message": "Autentimiskood saadeti meili teel. Seda saab registreerimiseks kasutada ainult üks kord ja piiratud aja jooksul."
      },
      "sendEmail": {
        "message": "Autentimiskood saadeti meili teel. Seda saab registreerimiseks kasutada ainult üks kord ja piiratud aja jooksul.",
        "success": {
          "message": "Autentimiskood on saadetud e-kirjaga aadressile {primaryEmailAddressPlaceholder}.",
          "title": "Edukas tegevus"
        },
        "title": "Edukas tegevus"
      }
    },
    "resendEmail": {
      "buttonLabel": "Saada uuesti"
    },
    "unlockWith": {
      "label": "Sisestage ja kinnitage saadud autentimiskood."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Sisestage autentimiskood",
        "label": "E-postist pärinev autentimiskood"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Saada uuesti {secondsPlaceholder} sekundi pärast"
    },
    "emailOTPForm": {
      "title": "Autentimiskood e-posti teel"
    },
    "confirmCode": {
      "buttonLabel": "Kinnita"
    }
  },
  "fi-FI": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Virhe",
        "message": "Todennuskoodia ei voitu vahvistaa."
      },
      "verifyEmailOTP": {
        "message": "Todennuskoodia ei voitu vahvistaa.",
        "title": "Virhe",
        "asyncError": {
          "message": "Todennuskoodi on virheellinen",
          "title": "Virhe"
        }
      },
      "emailSent": {
        "title": "Onnistunut toiminta",
        "message": "Todennuskoodi lähetettiin sähköpostitse. Sitä voidaan käyttää vain kerran ja rajoitetun ajan rekisteröintiä varten."
      },
      "sendEmail": {
        "message": "Todennuskoodi lähetettiin sähköpostitse. Sitä voidaan käyttää vain kerran ja rajoitetun ajan rekisteröintiä varten.",
        "success": {
          "message": "Autentikointikoodi on lähetetty sähköpostitse osoitteeseen {primaryEmailAddressPlaceholder}.",
          "title": "Onnistunut toiminta"
        },
        "title": "Onnistunut toiminta"
      }
    },
    "resendEmail": {
      "buttonLabel": "Lähetä uudestaan"
    },
    "unlockWith": {
      "label": "Syötä ja vahvista vastaanotettu todennuskoodi."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Kirjoita tunnistekoodi",
        "label": "Autentikointikoodi sähköpostista"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Lähetä uudelleen {secondsPlaceholder} sekunnin kuluttua"
    },
    "emailOTPForm": {
      "title": "Todennuskoodi sähköpostitse"
    },
    "confirmCode": {
      "buttonLabel": "Vahvista"
    }
  },
  "fr-BE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Erreur",
        "message": "Le code d'authentification n'a pas pu être confirmé."
      },
      "verifyEmailOTP": {
        "message": "Le code d'authentification n'a pas pu être confirmé.",
        "title": "Erreur",
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      },
      "emailSent": {
        "title": "Action réussie",
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement."
      },
      "sendEmail": {
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement.",
        "success": {
          "message": "Le code d'authentification a été envoyé par e-mail à {primaryEmailAddressPlaceholder}.",
          "title": "Action réussie"
        },
        "title": "Action réussie"
      }
    },
    "resendEmail": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Entrez le code d'authentification",
        "label": "Code d'authentification de l'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    },
    "emailOTPForm": {
      "title": "Code d'authentification par email"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-CH": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "Le code d'authentification a été envoyé par e-mail à {primaryEmailAddressPlaceholder}.",
          "title": "Action réussie"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    },
    "emailOTPForm": {
      "title": "Code d'authentification par email"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-FR": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Erreur",
        "message": "Le code d'authentification n'a pas pu être confirmé."
      },
      "verifyEmailOTP": {
        "message": "Le code d'authentification n'a pas pu être confirmé.",
        "title": "Erreur",
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      },
      "emailSent": {
        "title": "Action réussie",
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement."
      },
      "sendEmail": {
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement.",
        "success": {
          "message": "Le code d'authentification a été envoyé par e-mail à {primaryEmailAddressPlaceholder}.",
          "title": "Action réussie"
        },
        "title": "Action réussie"
      }
    },
    "resendEmail": {
      "buttonLabel": "Envoyer à nouveau"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    },
    "emailOTPForm": {
      "title": "Code d'authentification par email"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-LU": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Erreur",
        "message": "Le code d'authentification n'a pas pu être confirmé."
      },
      "verifyEmailOTP": {
        "message": "Le code d'authentification n'a pas pu être confirmé.",
        "title": "Erreur",
        "asyncError": {
          "message": "Le code d'authentification est invalide",
          "title": "Erreur"
        }
      },
      "emailSent": {
        "title": "Action réussie",
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement."
      },
      "sendEmail": {
        "message": "Le code d'authentification a été envoyé par e-mail. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour l'enregistrement.",
        "success": {
          "message": "Le code d'authentification a été envoyé par e-mail à {primaryEmailAddressPlaceholder}.",
          "title": "Action réussie"
        },
        "title": "Action réussie"
      }
    },
    "resendEmail": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    },
    "emailOTPForm": {
      "title": "Code d'authentification par email"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "ga-IE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Earráid",
        "message": "Níorbh fhéidir an cód fíordheimhnithe a dhearbhú."
      },
      "verifyEmailOTP": {
        "message": "Níorbh fhéidir an cód fíordheimhnithe a dhearbhú.",
        "title": "Earráid",
        "asyncError": {
          "message": "Tá an cód fíordheimhnithe neamhbhailí",
          "title": "Earráid"
        }
      },
      "emailSent": {
        "title": "Gníomh rathúil",
        "message": "Seoladh an cód fíordheimhnithe trí ríomhphost. Ní féidir é a úsáid ach uair amháin agus laistigh de thréimhse teoranta ama le haghaidh clárúcháin."
      },
      "sendEmail": {
        "message": "Seoladh an cód fíordheimhnithe trí ríomhphost. Ní féidir é a úsáid ach uair amháin agus laistigh de thréimhse teoranta ama le haghaidh clárúcháin.",
        "success": {
          "message": "Cuireadh cód fíordheimhnithe tríd an ríomhphost chuig {primaryEmailAddressPlaceholder}.",
          "title": "Gníomh rathúil"
        },
        "title": "Gníomh rathúil"
      }
    },
    "resendEmail": {
      "buttonLabel": "Seol arís"
    },
    "unlockWith": {
      "label": "Cuir isteach agus deimhnigh an cód fíordheimhnithe a fuarthas."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Cuir isteach cód fíordheimhnithe",
        "label": "Cód authenachta ón ríomhphost"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Athseoladh i {secondsPlaceholder} soicind"
    },
    "emailOTPForm": {
      "title": "Cód fíordheimhnithe trí ríomhphost"
    },
    "confirmCode": {
      "buttonLabel": "Deimhnigh"
    }
  },
  "he-IL": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "שְׁגִיאָה",
        "message": "לא ניתן היה לאשר את קוד האימות."
      },
      "verifyEmailOTP": {
        "message": "לא ניתן היה לאשר את קוד האימות.",
        "title": "שְׁגִיאָה",
        "asyncError": {
          "message": "קוד האימות אינו חוקי",
          "title": "שְׁגִיאָה"
        }
      },
      "emailSent": {
        "title": "פעולה מוצלחת",
        "message": "קוד האימות נשלח בדוא＂ל. ניתן להשתמש בו רק פעם אחת ובזמן מוגבל לרישום."
      },
      "sendEmail": {
        "message": "קוד האימות נשלח בדוא＂ל. ניתן להשתמש בו רק פעם אחת ובזמן מוגבל לרישום.",
        "success": {
          "message": "קוד האימות נשלח בדוא״ל לכתובת {primaryEmailAddressPlaceholder}.",
          "title": "פעולה מוצלחת"
        },
        "title": "פעולה מוצלחת"
      }
    },
    "resendEmail": {
      "buttonLabel": "שלח שוב"
    },
    "unlockWith": {
      "label": "הזן ואשר את קוד האימות שהתקבל."
    },
    "emailOTP": {
      "input": {
        "placeholder": "הזן קוד אימות",
        "label": "קוד אימות מהאימייל"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "שלח מחדש בעוד {secondsPlaceholder} שניות"
    },
    "emailOTPForm": {
      "title": "קוד אימות במייל"
    },
    "confirmCode": {
      "buttonLabel": "לְאַשֵׁר"
    }
  },
  "hi-IN": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "प्रमाणीकरण कोड अमान्य है",
          "title": "त्रुटि"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "{primaryEmailAddressPlaceholder} को ईमेल के माध्यम से प्रमाणीकरण कोड भेजा गया है।",
          "title": "सफल कार्रवाई"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "फिर से भेजें"
    },
    "unlockWith": {
      "label": "प्राप्त प्रमाणीकरण कोड दर्ज करें और पुष्टि करें।"
    },
    "emailOTP": {
      "input": {
        "placeholder": "प्रमाणीकरण कोड दर्ज करें",
        "label": "ईमेल से प्रमाणीकरण कोड"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder} सेकंड में पुन: भेजें"
    },
    "emailOTPForm": {
      "title": "ईमेल द्वारा प्रमाणीकरण कोड"
    },
    "confirmCode": {
      "buttonLabel": "पुष्टि करें"
    }
  },
  "hr-HR": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Pogreška",
        "message": "Nije moguće potvrditi autentifikacijski kod."
      },
      "verifyEmailOTP": {
        "message": "Nije moguće potvrditi autentifikacijski kod.",
        "title": "Pogreška",
        "asyncError": {
          "message": "Kod za autentifikaciju je nevažeći",
          "title": "Pogreška"
        }
      },
      "emailSent": {
        "title": "Uspješna akcija",
        "message": "Kod za provjeru autentičnosti poslan je e-poštom. Može se koristiti samo jednom i unutar ograničenog vremena za registraciju."
      },
      "sendEmail": {
        "message": "Kod za provjeru autentičnosti poslan je e-poštom. Može se koristiti samo jednom i unutar ograničenog vremena za registraciju.",
        "success": {
          "message": "Autentikacijski kod poslan je e-mailom na {primaryEmailAddressPlaceholder}.",
          "title": "Uspješna akcija"
        },
        "title": "Uspješna akcija"
      }
    },
    "resendEmail": {
      "buttonLabel": "Pošalji opet"
    },
    "unlockWith": {
      "label": "Unesite i potvrdite primljeni autentifikacijski kod."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Unesite autentifikacijski kod",
        "label": "Autentifikacijski kôd iz e-maila"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    },
    "emailOTPForm": {
      "title": "Autentifikacijski kod e-poštom"
    },
    "confirmCode": {
      "buttonLabel": "Potvrda"
    }
  },
  "hu-HU": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Hiba",
        "message": "A hitelesítő kódot nem sikerült megerősíteni."
      },
      "verifyEmailOTP": {
        "message": "A hitelesítő kódot nem sikerült megerősíteni.",
        "title": "Hiba",
        "asyncError": {
          "message": "Az hitelesítési kód érvénytelen",
          "title": "Hiba"
        }
      },
      "emailSent": {
        "title": "Sikeres művelet",
        "message": "A hitelesítési kódot e-mailben küldték el. Csak egyszer és korlátozott ideig használható fel regisztrációhoz."
      },
      "sendEmail": {
        "message": "A hitelesítési kódot e-mailben küldték el. Csak egyszer és korlátozott ideig használható fel regisztrációhoz.",
        "success": {
          "message": "Az hitelesítő kódot emailben küldtük el a {primaryEmailAddressPlaceholder} címre.",
          "title": "Sikeres művelet"
        },
        "title": "Sikeres művelet"
      }
    },
    "resendEmail": {
      "buttonLabel": "Küldd újra"
    },
    "unlockWith": {
      "label": "Írja be és erősítse meg a kapott hitelesítési kódot."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Adja meg az hitelesítő kódot",
        "label": "Hitelesítő kód az e-mailből"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Újraküldés {secondsPlaceholder} másodperc múlva"
    },
    "emailOTPForm": {
      "title": "Hitelesítési kód e-mailben"
    },
    "confirmCode": {
      "buttonLabel": "Megerősít"
    }
  },
  "it-CH": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "Il codice di autenticazione non è valido",
          "title": "Errore"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "Il codice di autenticazione è stato inviato tramite email a {primaryEmailAddressPlaceholder}.",
          "title": "Azione di successo"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Invia di nuovo"
    },
    "unlockWith": {
      "label": "Inserisci e conferma il codice di autenticazione ricevuto."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione",
        "label": "Codice di autenticazione dall'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    },
    "emailOTPForm": {
      "title": "Codice di autenticazione via e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Confermare"
    }
  },
  "it-IT": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Errore",
        "message": "Non è stato possibile confermare il codice di autenticazione."
      },
      "verifyEmailOTP": {
        "message": "Non è stato possibile confermare il codice di autenticazione.",
        "title": "Errore",
        "asyncError": {
          "message": "Il codice di autenticazione non è valido",
          "title": "Errore"
        }
      },
      "emailSent": {
        "title": "Azione riuscita",
        "message": "Il codice di autenticazione è stato inviato via e-mail. Può essere utilizzato solo una volta ed entro un tempo limitato per la registrazione."
      },
      "sendEmail": {
        "message": "Il codice di autenticazione è stato inviato via e-mail. Può essere utilizzato solo una volta ed entro un tempo limitato per la registrazione.",
        "success": {
          "message": "Il codice di autenticazione è stato inviato per email a {primaryEmailAddressPlaceholder}.",
          "title": "Azione riuscita"
        },
        "title": "Azione riuscita"
      }
    },
    "resendEmail": {
      "buttonLabel": "Invia di nuovo"
    },
    "unlockWith": {
      "label": "Inserisci e conferma il codice di autenticazione ricevuto."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione",
        "label": "Codice di autenticazione dall'email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    },
    "emailOTPForm": {
      "title": "Codice di autenticazione via e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Conferma"
    }
  },
  "ja-JP": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "エラー",
        "message": "認証コードが確認できませんでした。"
      },
      "verifyEmailOTP": {
        "message": "認証コードが確認できませんでした。",
        "title": "エラー",
        "asyncError": {
          "message": "認証コードが無効です",
          "title": "エラー"
        }
      },
      "emailSent": {
        "title": "成功したアクション",
        "message": "認証コードはメールで送信されました。 1回のみ利用可能で、登録の制限時間内に利用できます。"
      },
      "sendEmail": {
        "message": "認証コードはメールで送信されました。 1回のみ利用可能で、登録の制限時間内に利用できます。",
        "success": {
          "message": "認証コードが{primaryEmailAddressPlaceholder}に電子メールで送信されました。",
          "title": "成功したアクション"
        },
        "title": "成功したアクション"
      }
    },
    "resendEmail": {
      "buttonLabel": "もう一度送信"
    },
    "unlockWith": {
      "label": "受信した認証コードを入力して確認します。"
    },
    "emailOTP": {
      "input": {
        "placeholder": "認証コードを入力してください",
        "label": "メールからの認証コード"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒後に再送信"
    },
    "emailOTPForm": {
      "title": "メールによる認証コード"
    },
    "confirmCode": {
      "buttonLabel": "確認"
    }
  },
  "ko-KR": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "인증 코드가 유효하지 않습니다",
          "title": "오류"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "인증 코드가 {primaryEmailAddressPlaceholder}로 이메일로 전송되었습니다.",
          "title": "성공적인 액션"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "다시 보내기"
    },
    "unlockWith": {
      "label": "받은 인증 코드를 입력하고 확인하십시오."
    },
    "emailOTP": {
      "input": {
        "placeholder": "인증 코드를 입력하세요",
        "label": "이메일로부터의 인증 코드"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder}초 후에 재전송"
    },
    "emailOTPForm": {
      "title": "이메일을 통한 인증 코드"
    },
    "confirmCode": {
      "buttonLabel": "확인"
    }
  },
  "lt-LT": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Klaida",
        "message": "Nepavyko patvirtinti autentifikavimo kodo."
      },
      "verifyEmailOTP": {
        "message": "Nepavyko patvirtinti autentifikavimo kodo.",
        "title": "Klaida",
        "asyncError": {
          "message": "Autentifikacijos kodas netinkamas",
          "title": "Klaida"
        }
      },
      "emailSent": {
        "title": "Sėkmingas veiksmas",
        "message": "Autentifikavimo kodas buvo išsiųstas el. paštu. Jį galima naudoti tik vieną kartą ir per ribotą registracijos laiką."
      },
      "sendEmail": {
        "message": "Autentifikavimo kodas buvo išsiųstas el. paštu. Jį galima naudoti tik vieną kartą ir per ribotą registracijos laiką.",
        "success": {
          "message": "Autentifikacijos kodas išsiųstas el. paštu į {primaryEmailAddressPlaceholder}.",
          "title": "Sėkmingas veiksmas"
        },
        "title": "Sėkmingas veiksmas"
      }
    },
    "resendEmail": {
      "buttonLabel": "Siųsti dar kartą"
    },
    "unlockWith": {
      "label": "Įveskite ir patvirtinkite gautą autentifikavimo kodą."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Įveskite autentifikacijos kodą",
        "label": "Autentifikavimo kodas iš el. pašto"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Persiųsti po {secondsPlaceholder} sekundžių"
    },
    "emailOTPForm": {
      "title": "Autentifikavimo kodas el. Paštu"
    },
    "confirmCode": {
      "buttonLabel": "Patvirtinti"
    }
  },
  "lv-LV": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Kļūda",
        "message": "Nevarēja apstiprināt autentifikācijas kodu."
      },
      "verifyEmailOTP": {
        "message": "Nevarēja apstiprināt autentifikācijas kodu.",
        "title": "Kļūda",
        "asyncError": {
          "message": "Autentifikācijas kods ir nederīgs",
          "title": "Kļūda"
        }
      },
      "emailSent": {
        "title": "Veiksmīga darbība",
        "message": "Autentifikācijas kods tika nosūtīts pa e-pastu. To var izmantot tikai vienu reizi un ierobežotā reģistrācijas laikā."
      },
      "sendEmail": {
        "message": "Autentifikācijas kods tika nosūtīts pa e-pastu. To var izmantot tikai vienu reizi un ierobežotā reģistrācijas laikā.",
        "success": {
          "message": "Autentifikācijas kods ir nosūtīts uz {primaryEmailAddressPlaceholder} pa e-pastu.",
          "title": "Veiksmīga darbība"
        },
        "title": "Veiksmīga darbība"
      }
    },
    "resendEmail": {
      "buttonLabel": "Sūtīt vēlreiz"
    },
    "unlockWith": {
      "label": "Ievadiet un apstipriniet saņemto autentifikācijas kodu."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Ievadiet autentifikācijas kodu",
        "label": "Autentifikācijas kods no e-pasta"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Atkārtoti nosūtīt pēc {secondsPlaceholder} sekundēm"
    },
    "emailOTPForm": {
      "title": "Autentifikācijas kods pa e-pastu"
    },
    "confirmCode": {
      "buttonLabel": "Apstiprināt"
    }
  },
  "mt-MT": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Żball",
        "message": "Il-kodiċi ta' awtentikazzjoni ma setax jiġi kkonfermat."
      },
      "verifyEmailOTP": {
        "message": "Il-kodiċi ta' awtentikazzjoni ma setax jiġi kkonfermat.",
        "title": "Żball",
        "asyncError": {
          "message": "Il-kodiċi ta' awtentiċità mhux validu",
          "title": "Żball"
        }
      },
      "emailSent": {
        "title": "Azzjoni ta' suċċess",
        "message": "Il-kodiċi ta' awtentikazzjoni ntbagħat permezz ta' email. Jista' jintuża darba biss u fi żmien limitat għar-reġistrazzjoni."
      },
      "sendEmail": {
        "message": "Il-kodiċi ta' awtentikazzjoni ntbagħat permezz ta' email. Jista' jintuża darba biss u fi żmien limitat għar-reġistrazzjoni.",
        "success": {
          "message": "Il-kodiċi ta' awtentikazzjoni ġie mibgħut permezz ta' email għal {primaryEmailAddressPlaceholder}.",
          "title": "Azzjoni ta' suċċess"
        },
        "title": "Azzjoni ta' suċċess"
      }
    },
    "resendEmail": {
      "buttonLabel": "Ibgħat mill-ġdid"
    },
    "unlockWith": {
      "label": "Daħħal u kkonferma l-kodiċi ta 'awtentikazzjoni riċevut."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Idħol il-kodiċi ta' awtentikazzjoni",
        "label": "Kodiċi ta' awtentikazzjoni mill-email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Ibgħat mill-ġdid fi {secondsPlaceholder} sekondi"
    },
    "emailOTPForm": {
      "title": "Kodiċi ta 'awtentikazzjoni bl-email"
    },
    "confirmCode": {
      "buttonLabel": "Ikkonferma"
    }
  },
  "nb-NO": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "Autentiseringskoden er ugyldig",
          "title": "Feil"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "Bekreftelseskoden er sendt på e-post til {primaryEmailAddressPlaceholder}.",
          "title": "Vellykket handling"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Send igjen"
    },
    "unlockWith": {
      "label": "Skriv inn og bekreft autentiseringskoden du mottok."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Skriv inn godkjenningskode",
        "label": "Autentiseringskode fra e-post"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Send på nytt om {secondsPlaceholder} sekunder"
    },
    "emailOTPForm": {
      "title": "Autentiseringskode via e-post"
    },
    "confirmCode": {
      "buttonLabel": "Bekrefte"
    }
  },
  "nl-BE": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "De verificatiecode is ongeldig",
          "title": "Fout"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "De authenticatiecode is per e-mail verzonden naar {primaryEmailAddressPlaceholder}.",
          "title": "Geslaagde actie"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "unlockWith": {
      "label": "Voer de ontvangen authenticatiecode in en bevestig deze."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in",
        "label": "Authenticatiecode van e-mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    },
    "emailOTPForm": {
      "title": "Authenticatiecode per e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    }
  },
  "nl-NL": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fout",
        "message": "De authencitatiecode kon niet worden bevestigd."
      },
      "verifyEmailOTP": {
        "message": "De authencitatiecode kon niet worden bevestigd.",
        "title": "Fout",
        "asyncError": {
          "message": "De verificatiecode is ongeldig",
          "title": "Fout"
        }
      },
      "emailSent": {
        "title": "Geslaagde actie",
        "message": "De authenticatiecode is per e-mail verzonden. Het kan slechts één keer en binnen een beperkte tijd worden gebruikt voor registratie."
      },
      "sendEmail": {
        "message": "De authenticatiecode is per e-mail verzonden. Het kan slechts één keer en binnen een beperkte tijd worden gebruikt voor registratie.",
        "success": {
          "message": "De authenticatiecode is per e-mail verzonden naar {primaryEmailAddressPlaceholder}.",
          "title": "Geslaagde actie"
        },
        "title": "Geslaagde actie"
      }
    },
    "resendEmail": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "unlockWith": {
      "label": "Voer de ontvangen authenticatiecode in en bevestig deze."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in",
        "label": "Authenticatiecode van e-mail"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    },
    "emailOTPForm": {
      "title": "Authenticatiecode per e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    }
  },
  "pl-PL": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Błąd",
        "message": "Nie udało się potwierdzić kodu uwierzytelniającego."
      },
      "verifyEmailOTP": {
        "message": "Nie udało się potwierdzić kodu uwierzytelniającego.",
        "title": "Błąd",
        "asyncError": {
          "message": "Kod uwierzytelniania jest nieprawidłowy",
          "title": "Błąd"
        }
      },
      "emailSent": {
        "title": "Czynność zakończona powodzeniem",
        "message": "Kod uwierzytelniający został wysłany e-mailem. Można go użyć tylko raz iw ograniczonym czasie do rejestracji."
      },
      "sendEmail": {
        "message": "Kod uwierzytelniający został wysłany e-mailem. Można go użyć tylko raz iw ograniczonym czasie do rejestracji.",
        "success": {
          "message": "Kod uwierzytelniający został wysłany e-mailem na {primaryEmailAddressPlaceholder}.",
          "title": "Czynność zakończona powodzeniem"
        },
        "title": "Czynność zakończona powodzeniem"
      }
    },
    "resendEmail": {
      "buttonLabel": "Wyślij ponownie"
    },
    "unlockWith": {
      "label": "Wprowadź i potwierdź otrzymany kod uwierzytelniający."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Wprowadź kod uwierzytelniania",
        "label": "Kod uwierzytelniania z e-maila"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Wyślij ponownie za {secondsPlaceholder} sekund"
    },
    "emailOTPForm": {
      "title": "Kod uwierzytelniający przez e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Potwierdź"
    }
  },
  "pt-PT": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Erro",
        "message": "Não foi possível confirmar o código de autenticação."
      },
      "verifyEmailOTP": {
        "message": "Não foi possível confirmar o código de autenticação.",
        "title": "Erro",
        "asyncError": {
          "message": "O código de autenticação é inválido",
          "title": "Erro"
        }
      },
      "emailSent": {
        "title": "Ação bem-sucedida",
        "message": "O código de autenticação foi enviado por e-mail. Ele só pode ser usado uma vez e dentro de um tempo limitado para registro."
      },
      "sendEmail": {
        "message": "O código de autenticação foi enviado por e-mail. Ele só pode ser usado uma vez e dentro de um tempo limitado para registro.",
        "success": {
          "message": "O código de autenticação foi enviado por e-mail para {primaryEmailAddressPlaceholder}.",
          "title": "Ação bem-sucedida"
        },
        "title": "Ação bem-sucedida"
      }
    },
    "resendEmail": {
      "buttonLabel": "Envie novamente"
    },
    "unlockWith": {
      "label": "Digite e confirme o código de autenticação recebido."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Insira o código de autenticação",
        "label": "Código de autenticação do email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Reenviar em {secondsPlaceholder} segundos"
    },
    "emailOTPForm": {
      "title": "Código de autenticação por email"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    }
  },
  "ro-RO": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Eroare",
        "message": "Codul de autentificare nu a putut fi confirmat."
      },
      "verifyEmailOTP": {
        "message": "Codul de autentificare nu a putut fi confirmat.",
        "title": "Eroare",
        "asyncError": {
          "message": "Codul de autentificare este invalid",
          "title": "Eroare"
        }
      },
      "emailSent": {
        "title": "Acțiune reușită",
        "message": "Codul de autentificare a fost trimis prin e-mail. Poate fi folosit o singură dată și într-un timp limitat pentru înregistrare."
      },
      "sendEmail": {
        "message": "Codul de autentificare a fost trimis prin e-mail. Poate fi folosit o singură dată și într-un timp limitat pentru înregistrare.",
        "success": {
          "message": "Codul de autentificare a fost trimis prin e-mail la {primaryEmailAddressPlaceholder}.",
          "title": "Acțiune reușită"
        },
        "title": "Acțiune reușită"
      }
    },
    "resendEmail": {
      "buttonLabel": "Trimite din nou"
    },
    "unlockWith": {
      "label": "Introduceți și confirmați codul de autentificare primit."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Introduceți codul de autentificare",
        "label": "Codul de autentificare din email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Retrimite în {secondsPlaceholder} secunde"
    },
    "emailOTPForm": {
      "title": "Cod de autentificare prin e-mail"
    },
    "confirmCode": {
      "buttonLabel": "Confirmare"
    }
  },
  "ru-RU": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Ошибка",
        "message": "Код аутентификации не подтвержден."
      },
      "verifyEmailOTP": {
        "message": "Код аутентификации не подтвержден.",
        "title": "Ошибка",
        "asyncError": {
          "message": "Код аутентификации недействителен",
          "title": "Ошибка"
        }
      },
      "emailSent": {
        "title": "Успешное действие",
        "message": "Код аутентификации был отправлен по электронной почте. Его можно использовать только один раз и в течение ограниченного времени регистрации."
      },
      "sendEmail": {
        "message": "Код аутентификации был отправлен по электронной почте. Его можно использовать только один раз и в течение ограниченного времени регистрации.",
        "success": {
          "message": "Код аутентификации был отправлен по электронной почте на {primaryEmailAddressPlaceholder}.",
          "title": "Успешное действие"
        },
        "title": "Успешное действие"
      }
    },
    "resendEmail": {
      "buttonLabel": "Отправь еще раз"
    },
    "unlockWith": {
      "label": "Введите и подтвердите полученный код аутентификации."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Введите код аутентификации",
        "label": "Код аутентификации из электронной почты"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Отправить снова через {secondsPlaceholder} секунд"
    },
    "emailOTPForm": {
      "title": "Код аутентификации по электронной почте"
    },
    "confirmCode": {
      "buttonLabel": "Подтвердить"
    }
  },
  "sk-SK": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Chyba",
        "message": "Overovací kód nebolo možné potvrdiť."
      },
      "verifyEmailOTP": {
        "message": "Overovací kód nebolo možné potvrdiť.",
        "title": "Chyba",
        "asyncError": {
          "message": "Autentifikačný kód je neplatný",
          "title": "Chyba"
        }
      },
      "emailSent": {
        "title": "Úspešná akcia",
        "message": "Overovací kód bol odoslaný e-mailom. Môže byť použitý iba raz a v obmedzenom čase na registráciu."
      },
      "sendEmail": {
        "message": "Overovací kód bol odoslaný e-mailom. Môže byť použitý iba raz a v obmedzenom čase na registráciu.",
        "success": {
          "message": "Autentifikačný kód bol odoslaný e-mailom na adresu {primaryEmailAddressPlaceholder}.",
          "title": "Úspešná akcia"
        },
        "title": "Úspešná akcia"
      }
    },
    "resendEmail": {
      "buttonLabel": "Poslať znova"
    },
    "unlockWith": {
      "label": "Zadajte a potvrďte prijatý overovací kód."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Zadajte overovací kód",
        "label": "Autentifikačný kód z e-mailu"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Znovu poslať o {secondsPlaceholder} sekundy"
    },
    "emailOTPForm": {
      "title": "Autentifikačný kód e-mailom"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdiť"
    }
  },
  "sl-SI": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Napaka",
        "message": "Kode za preverjanje pristnosti ni bilo mogoče potrditi."
      },
      "verifyEmailOTP": {
        "message": "Kode za preverjanje pristnosti ni bilo mogoče potrditi.",
        "title": "Napaka",
        "asyncError": {
          "message": "Koda za overitev je neveljavna",
          "title": "Napaka"
        }
      },
      "emailSent": {
        "title": "Uspešno opravljeno",
        "message": "Koda za preverjanje pristnosti je bila poslana po e-pošti. Uporablja se lahko samo enkrat in v omejenem času za registracijo."
      },
      "sendEmail": {
        "message": "Koda za preverjanje pristnosti je bila poslana po e-pošti. Uporablja se lahko samo enkrat in v omejenem času za registracijo.",
        "success": {
          "message": "Koda za overitev je bila poslana po e-pošti na {primaryEmailAddressPlaceholder}.",
          "title": "Uspešno opravljeno"
        },
        "title": "Uspešno opravljeno"
      }
    },
    "resendEmail": {
      "buttonLabel": "Pošlji ponovno"
    },
    "unlockWith": {
      "label": "Vnesite in potrdite prejeto kodo za preverjanje pristnosti."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Vnesite kodo za preverjanje pristnosti",
        "label": "Avtentikacijska koda iz e-pošte"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Ponovno pošlji čez {secondsPlaceholder} sekund"
    },
    "emailOTPForm": {
      "title": "Koda za preverjanje pristnosti po e-pošti"
    },
    "confirmCode": {
      "buttonLabel": "Potrdi"
    }
  },
  "sv-SE": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Fel",
        "message": "Autentiseringskoden kunde inte bekräftas."
      },
      "verifyEmailOTP": {
        "message": "Autentiseringskoden kunde inte bekräftas.",
        "title": "Fel",
        "asyncError": {
          "message": "Autentiseringskoden är ogiltig",
          "title": "Fel"
        }
      },
      "emailSent": {
        "title": "Åtgärd utförd",
        "message": "Autentiseringskoden skickades via e-post. Den kan endast användas en gång och inom en begränsad tid för registrering."
      },
      "sendEmail": {
        "message": "Autentiseringskoden skickades via e-post. Den kan endast användas en gång och inom en begränsad tid för registrering.",
        "success": {
          "message": "Autentiseringskoden har skickats med e-post till {primaryEmailAddressPlaceholder}.",
          "title": "Åtgärd utförd"
        },
        "title": "Åtgärd utförd"
      }
    },
    "resendEmail": {
      "buttonLabel": "Skicka igen"
    },
    "unlockWith": {
      "label": "Ange och bekräfta den mottagna autentiseringskoden."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Ange autentiseringskod",
        "label": "Autentiseringskod från e-post"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Skicka igen om {secondsPlaceholder} sekunder"
    },
    "emailOTPForm": {
      "title": "Autentiseringskod via e-post"
    },
    "confirmCode": {
      "buttonLabel": "Bekräfta"
    }
  },
  "th-TH": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "รหัสการตรวจสอบไม่ถูกต้อง",
          "title": "ความผิดพลาด"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "รหัสการรับรองความถูกต้องได้ถูกส่งไปทางอีเมลไปยัง {primaryEmailAddressPlaceholder}",
          "title": "การดำเนินการที่ประสบความสำเร็จ"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "ส่งอีกครั้ง"
    },
    "unlockWith": {
      "label": "ป้อนและยืนยันรหัสการรับรองที่ได้รับ"
    },
    "emailOTP": {
      "input": {
        "placeholder": "ป้อนรหัสยืนยัน",
        "label": "รหัสการยืนยันจากอีเมล"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "ส่งใหม่ใน {secondsPlaceholder} วินาที"
    },
    "emailOTPForm": {
      "title": "รหัสการยืนยันผ่านอีเมล"
    },
    "confirmCode": {
      "buttonLabel": "ยืนยัน"
    }
  },
  "tr-CY": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Hata",
        "message": "Kimlik doğrulama kodu onaylanamadı."
      },
      "verifyEmailOTP": {
        "message": "Kimlik doğrulama kodu onaylanamadı.",
        "title": "Hata",
        "asyncError": {
          "message": "Doğrulama kodu geçersiz",
          "title": "Hata"
        }
      },
      "emailSent": {
        "title": "Başarılı eylem",
        "message": "Kimlik doğrulama kodu e-posta yoluyla gönderildi. Kayıt için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir."
      },
      "sendEmail": {
        "message": "Kimlik doğrulama kodu e-posta yoluyla gönderildi. Kayıt için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir.",
        "success": {
          "message": "{primaryEmailAddressPlaceholder} adresine e-posta ile doğrulama kodu gönderilmiştir.",
          "title": "Başarılı eylem"
        },
        "title": "Başarılı eylem"
      }
    },
    "resendEmail": {
      "buttonLabel": "Tekrar gönderin"
    },
    "unlockWith": {
      "label": "Alınan doğrulama kodunu girin ve onaylayın."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin",
        "label": "E-postadan doğrulama kodu"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    },
    "emailOTPForm": {
      "title": "E-posta ile kimlik doğrulama kodu"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    }
  },
  "tr-TR": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Hata",
        "message": "Kimlik doğrulama kodu onaylanamadı."
      },
      "verifyEmailOTP": {
        "message": "Kimlik doğrulama kodu onaylanamadı.",
        "title": "Hata",
        "asyncError": {
          "message": "Doğrulama kodu geçersiz",
          "title": "Hata"
        }
      },
      "emailSent": {
        "title": "Başarılı eylem",
        "message": "Kimlik doğrulama kodu e-posta yoluyla gönderildi. Kayıt için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir."
      },
      "sendEmail": {
        "message": "Kimlik doğrulama kodu e-posta yoluyla gönderildi. Kayıt için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir.",
        "success": {
          "message": "{primaryEmailAddressPlaceholder} adresine e-posta ile doğrulama kodu gönderilmiştir.",
          "title": "Başarılı eylem"
        },
        "title": "Başarılı eylem"
      }
    },
    "resendEmail": {
      "buttonLabel": "Tekrar gönderin"
    },
    "unlockWith": {
      "label": "Alınan doğrulama kodunu girin ve onaylayın."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin",
        "label": "E-postadan doğrulama kodu"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    },
    "emailOTPForm": {
      "title": "E-posta ile kimlik doğrulama kodu"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    }
  },
  "uk-UA": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "Помилка",
        "message": "Не вдалося підтвердити код автентифікації."
      },
      "verifyEmailOTP": {
        "message": "Не вдалося підтвердити код автентифікації.",
        "title": "Помилка",
        "asyncError": {
          "message": "Код автентифікації недійсний",
          "title": "Помилка"
        }
      },
      "emailSent": {
        "title": "Успішна акція",
        "message": "Код автентифікації надіслано електронною поштою. Його можна використовувати лише один раз і протягом обмеженого часу для реєстрації."
      },
      "sendEmail": {
        "message": "Код автентифікації надіслано електронною поштою. Його можна використовувати лише один раз і протягом обмеженого часу для реєстрації.",
        "success": {
          "message": "Код підтвердження був відправлено електронною поштою на {primaryEmailAddressPlaceholder}.",
          "title": "Успішна дія"
        },
        "title": "Успішна акція"
      }
    },
    "resendEmail": {
      "buttonLabel": "Надіслати знову"
    },
    "unlockWith": {
      "label": "Введіть і підтвердьте отриманий код автентифікації."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Введіть код автентифікації",
        "label": "Код автентифікації з електронної пошти"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Надіслати знову за {secondsPlaceholder} секунд"
    },
    "emailOTPForm": {
      "title": "Код автентифікації електронною поштою"
    },
    "confirmCode": {
      "buttonLabel": "Підтвердити"
    }
  },
  "vi-VN": {
    "notification": {
      "emailOTPNotVerified": {},
      "verifyEmailOTP": {
        "asyncError": {
          "message": "Mã xác thực không hợp lệ",
          "title": "Lỗi"
        }
      },
      "emailSent": {},
      "sendEmail": {
        "success": {
          "message": "Mã xác thực đã được gửi qua email đến {primaryEmailAddressPlaceholder}.",
          "title": "Hành động thành công"
        }
      }
    },
    "resendEmail": {
      "buttonLabel": "Gửi lại"
    },
    "unlockWith": {
      "label": "Nhập và xác nhận mã xác thực đã nhận."
    },
    "emailOTP": {
      "input": {
        "placeholder": "Nhập mã xác thực",
        "label": "Mã xác thực từ email"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "Gửi lại sau {secondsPlaceholder} giây"
    },
    "emailOTPForm": {
      "title": "Mã xác thực qua email"
    },
    "confirmCode": {
      "buttonLabel": "Xác nhận"
    }
  },
  "zh-CN": {
    "notification": {
      "emailOTPNotVerified": {
        "title": "错误",
        "message": "无法确认验证码。"
      },
      "verifyEmailOTP": {
        "message": "无法确认验证码。",
        "title": "错误",
        "asyncError": {
          "message": "认证代码无效",
          "title": "错误"
        }
      },
      "emailSent": {
        "title": "操作成功",
        "message": "验证码是通过电子邮件发送的。 注册后限时使用一次。"
      },
      "sendEmail": {
        "message": "验证码是通过电子邮件发送的。 注册后限时使用一次。",
        "success": {
          "message": "认证码已通过电子邮件发送到{primaryEmailAddressPlaceholder}。",
          "title": "操作成功"
        },
        "title": "操作成功"
      }
    },
    "resendEmail": {
      "buttonLabel": "重新发送"
    },
    "unlockWith": {
      "label": "输入并确认收到的验证码。"
    },
    "emailOTP": {
      "input": {
        "placeholder": "请输入认证码",
        "label": "电子邮件中的认证码"
      }
    },
    "resendEmailInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒后重新发送"
    },
    "emailOTPForm": {
      "title": "通过电子邮件验证代码"
    },
    "confirmCode": {
      "buttonLabel": "确认"
    }
  }
}
</i18n>
